import React, {useEffect, useRef, useState} from 'react'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {
    Button,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    Text,
    IconButton
} from '@salesforce/retail-react-app/app/components/shared/ui'

import PropTypes from 'prop-types'
import {CONFIRMATION_DIALOG_DEFAULT_CONFIG} from '@salesforce/retail-react-app/app/pages/account/constant'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {useIntl} from 'react-intl'
import {
    alternateActionButton,
    confirmationMessageStyle,
    confirmationMessageStyleFromCheckoutSignout,
    contentStyle,
    contentStyleFromCheckoutSignout,
    dialogStyleFromCheckoutSignout,
    dialogTitleStyle,
    primaryActionButton,
    buttonContainerStyle
} from './index.style'
import {closeButton} from '../addToCartModal/index.style'
import colors from '../../theme/foundations/colors'
import {pxToRem} from '../../utills/pxToRem'

const ConfirmationModal = ({
    dialogTitle = CONFIRMATION_DIALOG_DEFAULT_CONFIG.dialogTitle,
    confirmationMessage = CONFIRMATION_DIALOG_DEFAULT_CONFIG.confirmationMessage,
    primaryActionLabel = CONFIRMATION_DIALOG_DEFAULT_CONFIG.primaryActionLabel,
    primaryActionAriaLabel = CONFIRMATION_DIALOG_DEFAULT_CONFIG.primaryActionAriaLabel,
    alternateActionLabel = CONFIRMATION_DIALOG_DEFAULT_CONFIG.alternateActionLabel,
    alternateActionAriaLabel = CONFIRMATION_DIALOG_DEFAULT_CONFIG.alternateActionAriaLabel,
    hideAlternateAction = false,
    onPrimaryAction = noop,
    onAlternateAction = noop,
    isFromCheckoutSignOut = false,
    ...props
}) => {
    const {formatMessage} = useIntl()
    const cancelRef = useRef()
    const [showModal, setShowModal] = useState(false)

    const handleConfirmClick = () => {
        window.scrollTo({top: 0, behavior: 'smooth'})
        onPrimaryAction()
        props.onClose()
    }

    const handleAlternateActionClick = () => {
        onAlternateAction()
        props.onClose()
    }

    useEffect(() => {
        let timeout
        if (props.isOpen) {
            timeout = setTimeout(() => {
                setShowModal(true)
            }, 300)
        } else {
            setShowModal(false)
        }

        return () => clearTimeout(timeout)
    }, [props.isOpen])

    return (
        <>
            {showModal && (
                <AlertDialog
                    isOpen={props.isOpen}
                    isCentered
                    onClose={handleAlternateActionClick}
                    leastDestructiveRef={cancelRef}
                    closeOnOverlayClick={false}
                    {...props}
                >
                    <AlertDialogOverlay bg={`${colors.blackAlpha[630]}`} />
                    <AlertDialogContent
                        sx={isFromCheckoutSignOut ? contentStyleFromCheckoutSignout : contentStyle}
                    >
                        <AlertDialogHeader
                            sx={
                                isFromCheckoutSignOut
                                    ? dialogStyleFromCheckoutSignout
                                    : dialogTitleStyle
                            }
                        >
                            {formatMessage(dialogTitle)}
                        </AlertDialogHeader>
                        <IconButton
                            icon={
                                <img
                                    src={getAssetUrl('static/img/pdp/close.svg')}
                                    height="15"
                                    width="15"
                                    alt="Cavender's"
                                />
                            }
                            variant="unstyled"
                            onClick={handleAlternateActionClick}
                            sx={closeButton}
                        />
                        <AlertDialogBody padding="0px">
                            <Text
                                sx={
                                    isFromCheckoutSignOut
                                        ? confirmationMessageStyleFromCheckoutSignout
                                        : confirmationMessageStyle
                                }
                            >
                                {formatMessage(confirmationMessage)}
                            </Text>
                        </AlertDialogBody>

                        <AlertDialogFooter
                            sx={buttonContainerStyle}
                            padding="0px"
                            display={['grid', 'grid', 'flex', 'flex']}
                            marginTop={
                                isFromCheckoutSignOut
                                    ? [pxToRem(20), pxToRem(20), pxToRem(25), pxToRem(25)]
                                    : [pxToRem(20), pxToRem(20), pxToRem(45), pxToRem(45)]
                            }
                            gap={pxToRem(15)}
                        >
                            {!hideAlternateAction ? (
                                <Button
                                    sx={alternateActionButton}
                                    ref={cancelRef}
                                    variant="ghost"
                                    aria-label={formatMessage(alternateActionAriaLabel)}
                                    onClick={handleAlternateActionClick}
                                >
                                    {formatMessage(alternateActionLabel)}
                                </Button>
                            ) : null}
                            <Button
                                sx={primaryActionButton}
                                ref={cancelRef}
                                variant="solid"
                                onClick={handleConfirmClick}
                                aria-label={formatMessage(primaryActionAriaLabel)}
                            >
                                {formatMessage(primaryActionLabel)}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            )}
        </>
    )
}

ConfirmationModal.propTypes = {
    /**
     * Prop to check if modal is open
     */
    isOpen: PropTypes.bool.isRequired,
    /**
     * Callback invoked to open the modal
     */
    onOpen: PropTypes.func.isRequired,
    /**
     * Callback invoked to close the modal
     */
    onClose: PropTypes.func.isRequired,
    /**
     * Text to be displayed as modal header
     */
    dialogTitle: PropTypes.object,
    /**
     * Text to display in confirmation modal prompting user to pick an action
     */
    confirmationMessage: PropTypes.object,
    /**
     * Button Label for primary action in confirmation modal
     */
    primaryActionLabel: PropTypes.object,
    /**
     * Button aria Label for primary action
     */
    primaryActionAriaLabel: PropTypes.object,
    /**
     * Button Label for alternate or secondary action in confirmation modal
     */
    alternateActionLabel: PropTypes.object,
    /**
     * Button aria Label for alternate or secondary action in confirmation modal
     */
    alternateActionAriaLabel: PropTypes.object,
    /**
     * Action to execute if user selects primary action
     */
    onPrimaryAction: PropTypes.func,
    /**
     * Action to execute if user selects alternate or secondary action
     */
    onAlternateAction: PropTypes.func,
    /**
     * Flag to hide of show alternative button
     */
    hideAlternateAction: PropTypes.bool,
    isFromCheckoutSignOut: PropTypes.bool
}

export default ConfirmationModal
